import React, { useState, useEffect, useRef } from "react";
import { Row, Col, Card, Statistic, Table, Progress, Tag, Segmented, Button, Modal, Form, InputNumber, Select, Typography, Empty, Tooltip } from "antd";
import { Area, Pie } from "@ant-design/plots";

const { Text } = Typography;

const COLOR_PLATFORM = "#2a78d6";
const COLOR_BYOK = "#eb6834";

const PROVIDER_COLORS = {
  OpenAI: "#2a78d6",
  Anthropic: "#008300",
  "AWS Bedrock": "#eb6834",
  Azure: "#4a3aa7",
  Other: "#e87ba4",
};

function lighten(hex, amt) {
  const n = parseInt(hex.slice(1), 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  const mix = (c) => Math.round(c + (255 - c) * amt);
  return `#${((1 << 24) + (mix(r) << 16) + (mix(g) << 8) + mix(b)).toString(16).slice(1)}`;
}

function providerOf(model) {
  const m = (model || "").toLowerCase();
  if (/^(gpt-|o[1-9]|chatgpt)/.test(m)) return "OpenAI";
  if (/^(us\.|eu\.|apac\.)?(anthropic|amazon|meta|mistral|cohere)\./.test(m)) return "AWS Bedrock";
  if (/^claude/.test(m)) return "Anthropic";
  return "Other";
}

const RANGE_PRESETS = [
  { label: "7 days", value: 7 },
  { label: "30 days", value: 30 },
  { label: "90 days", value: 90 },
];

function today() {
  return new Date().toISOString().slice(0, 10);
}
function daysAgo(days) {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - (days - 1));
  return d.toISOString().slice(0, 10);
}

function usd(n) {
  const v = Number(n || 0);
  if (v === 0) return "$0";
  return v < 1 ? `$${v.toFixed(4)}` : `$${v.toFixed(2)}`;
}
function num(n) {
  return Number(n || 0).toLocaleString();
}

export default function AiUsage(props) {
  const { range, onRangeChange, summary, daily, byModel, byCapability, budget, savingBudget, permission, onSaveBudget } = props;
  const [preset, setPreset] = useState(30);
  const [budgetOpen, setBudgetOpen] = useState(false);
  const [form] = Form.useForm();

  const canEdit = permission && permission.canEdit;

  const prevSaving = useRef(false);
  useEffect(() => {
    if (prevSaving.current && !savingBudget) {
      setBudgetOpen(false);
    }
    prevSaving.current = savingBudget;
  }, [savingBudget]);

  const onPreset = (days) => {
    setPreset(days);
    onRangeChange({ from: daysAgo(days), to: today() });
  };

  const chartData = [];
  (daily || []).forEach((d) => {
    chartData.push({ day: d.day, type: "Platform", value: Number(d.costUsdPlatform || 0) });
    chartData.push({ day: d.day, type: "BYOK", value: Number(d.costUsdByok || 0) });
  });

  const dayCount = (daily || []).length;
  const VISIBLE_DAYS = 14;

  const areaConfig = {
    data: chartData,
    xField: "day",
    yField: "value",
    colorField: "type",
    seriesField: "type",
    stack: true,
    height: 260,
    legend: { color: { position: "top" } },
    scale: { color: { range: [COLOR_BYOK, COLOR_PLATFORM] } },
    axis: { y: { labelFormatter: (v) => usd(v) } },
    tooltip: { channel: "y", valueFormatter: (v) => usd(v) },
    ...(dayCount > VISIBLE_DAYS ? { scrollbar: { x: { ratio: VISIBLE_DAYS / dayCount } } } : {}),
  };

  const spendMix = (() => {
    const rows = (byModel || [])
      .map((m) => ({ model: m.model, provider: providerOf(m.model), value: Number(m.costUsdTotal || 0) }))
      .filter((d) => d.value > 0);
    const groups = {};
    rows.forEach((r) => {
      (groups[r.provider] = groups[r.provider] || []).push(r);
    });
    const totalOf = (list) => list.reduce((s, r) => s + r.value, 0);
    const providerOrder = Object.keys(groups).sort((a, b) => totalOf(groups[b]) - totalOf(groups[a]));
    const data = [];
    const range = [];
    const providerTotals = [];
    providerOrder.forEach((prov) => {
      const models = groups[prov].slice().sort((a, b) => b.value - a.value);
      const base = PROVIDER_COLORS[prov] || "#8a8a8a";
      providerTotals.push({ provider: prov, value: totalOf(models), color: base });
      models.forEach((r, i) => {
        data.push(r);
        range.push(lighten(base, models.length > 1 ? Math.min(0.55, i * 0.16) : 0));
      });
    });
    return { data, range, providerTotals };
  })();

  const spendMixConfig = {
    data: spendMix.data,
    angleField: "value",
    colorField: "model",
    innerRadius: 0.6,
    height: 260,
    label: false,
    legend: { color: { position: "bottom" } },
    scale: { color: { range: spendMix.range } },
    tooltip: {
      items: [
        { field: "provider", name: "Provider" },
        { field: "value", name: "Spend", valueFormatter: (v) => usd(v) },
      ],
    },
  };

  const openBudget = () => {
    form.setFieldsValue({
      budgetUsd: budget ? budget.budgetUsd : 0,
      enforcement: (budget && budget.enforcement) || "alert",
    });
    setBudgetOpen(true);
  };

  const submitBudget = async () => {
    const values = await form.validateFields();
    onSaveBudget({ budgetUsd: values.budgetUsd, enforcement: values.enforcement });
  };

  const budgetSet = budget && budget.budgetUsd > 0;
  const percent = budget ? Math.min(budget.percentUsed || 0, 100) : 0;
  const spentTotal = budget
    ? budget.spentUsdTotal != null
      ? budget.spentUsdTotal
      : Number(budget.spentUsdPlatform || 0) + Number(budget.spentUsdByok || 0)
    : 0;
  const budgetColor = !budgetSet ? undefined : budget.overBudget ? "#e34948" : percent >= 80 ? COLOR_BYOK : COLOR_PLATFORM;

  const breakdownColumns = (keyField, keyTitle) => [
    { title: keyTitle, dataIndex: keyField, key: keyField },
    { title: "Calls", dataIndex: "calls", key: "calls", align: "right", render: num },
    { title: "Tokens", dataIndex: "totalTokens", key: "totalTokens", align: "right", render: num },
    { title: "Platform", dataIndex: "costUsdPlatform", key: "costUsdPlatform", align: "right", render: usd },
    { title: "BYOK", dataIndex: "costUsdByok", key: "costUsdByok", align: "right", render: usd },
    { title: "Total", dataIndex: "costUsdTotal", key: "costUsdTotal", align: "right", render: (v) => <strong>{usd(v)}</strong> },
  ];

  const modelColumns = [
    { title: "Model", dataIndex: "model", key: "model" },
    { title: "Provider", key: "provider", render: (_t, r) => <Tag>{providerOf(r.model)}</Tag> },
    ...breakdownColumns("model", "Model").slice(1),
  ];

  return (
    <div style={{ padding: 16, height: "100%", overflowY: "auto" }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Typography.Title level={4} style={{ margin: 0 }}>
            AI Usage &amp; Cost
          </Typography.Title>
          <Text type="secondary">
            Token usage and spend across all providers. Cost is split by who pays: platform-billed vs your own keys (BYOK).
          </Text>
        </Col>
        <Col>
          <Segmented options={RANGE_PRESETS} value={preset} onChange={onPreset} />
        </Col>
      </Row>

      {/* KPI tiles */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title="Calls" value={num(summary && summary.calls)} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title="Total tokens" value={num(summary && summary.totalTokens)} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic
              title={<Tooltip title="Calls billed to the platform (platform key / IAM) — your chargeable cost.">Platform spend</Tooltip>}
              value={usd(summary && summary.costUsdPlatform)}
              valueStyle={{ color: COLOR_PLATFORM }}
            />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic
              title={<Tooltip title="Calls on a tenant's own key — billed to them by the provider (estimated).">BYOK usage (est.)</Tooltip>}
              value={usd(summary && summary.costUsdByok)}
              valueStyle={{ color: COLOR_BYOK }}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={16} align="stretch" style={{ marginBottom: 16 }}>
        {/* Daily spend */}
        <Col xs={24} lg={10}>
          <Card size="small" title="Daily spend" style={{ height: "100%" }}>
            {chartData.length ? <Area {...areaConfig} /> : (
              <div style={{ height: 260, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Empty description="No usage in this period" />
              </div>
            )}
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          <Card
            size="small"
            title="Spend by provider &amp; model"
            style={{ height: "100%" }}
            extra={<Text type="secondary" style={{ fontSize: 12 }}>slice = model · colour = provider</Text>}
          >
            {spendMix.data.length ? <Pie {...spendMixConfig} /> : (
              <div style={{ height: 260, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Empty description="No spend in this period" image={Empty.PRESENTED_IMAGE_SIMPLE} />
              </div>
            )}
          </Card>
        </Col>

        <Col xs={24} lg={6}>
          <Card
            size="small"
            title="Monthly budget"
            style={{ height: "100%" }}
            styles={{ body: { height: "calc(100% - 39px)", display: "flex", flexDirection: "column" } }}
            extra={canEdit && budgetSet ? <Button type="link" onClick={openBudget}>Edit</Button> : null}
          >
            {budgetSet ? (
              <>
                <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 10 }}>
                  <Progress type="dashboard" percent={percent} strokeColor={budgetColor} size={150} />
                  <div style={{ textAlign: "center" }}>
                    <div>
                      <Text strong style={{ fontSize: 16 }}>{usd(spentTotal)}</Text>
                      <Text type="secondary"> of {usd(budget.budgetUsd)}</Text>
                    </div>
                    <Text type="secondary" style={{ fontSize: 12 }}>total spend this month · {budget.month}</Text>
                    <div style={{ marginTop: 4 }}>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        platform {usd(budget.spentUsdPlatform)} · BYOK {usd(budget.spentUsdByok)}
                      </Text>
                    </div>
                  </div>
                  <div>
                    <Tag color={budget.enforcement === "block" ? "red" : "blue"}>
                      {budget.enforcement === "block" ? "Hard block over budget" : "Alert only"}
                    </Tag>
                    {budget.overBudget && <Tag color="volcano">Over budget</Tag>}
                  </div>
                </div>
                <Text type="secondary" style={{ fontSize: 12, textAlign: "center" }}>
                  Combined spend across all providers and keys (platform + BYOK).
                </Text>
              </>
            ) : (
              <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Empty description="No monthly budget set" image={Empty.PRESENTED_IMAGE_SIMPLE}>
                  {canEdit && <Button type="primary" onClick={openBudget}>Set budget</Button>}
                </Empty>
              </div>
            )}
          </Card>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col xs={24} lg={12}>
          <Card size="small" title="By model">
            <Table
              size="small"
              rowKey="model"
              dataSource={byModel || []}
              columns={modelColumns}
              pagination={false}
              scroll={{ x: "max-content" }}
              locale={{ emptyText: "No usage in this period" }}
            />
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card size="small" title="By capability (feature)">
            <Table
              size="small"
              rowKey="capability"
              dataSource={byCapability || []}
              columns={breakdownColumns("capability", "Capability")}
              pagination={false}
              scroll={{ x: "max-content" }}
              locale={{ emptyText: "No usage in this period" }}
            />
          </Card>
        </Col>
      </Row>

      <Modal
        title="Monthly AI budget"
        open={budgetOpen}
        onOk={submitBudget}
        onCancel={() => setBudgetOpen(false)}
        okText="Save"
        confirmLoading={savingBudget}
        cancelButtonProps={{ disabled: savingBudget }}
        maskClosable={!savingBudget}
        closable={!savingBudget}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="budgetUsd"
            label="Monthly budget (USD)"
            rules={[{ required: true, message: "Enter a monthly budget" }]}
            extra="Applies to total AI spend — platform-billed plus your own keys (BYOK). Set 0 to remove the budget."
          >
            <InputNumber min={0} step={1} style={{ width: "100%" }} prefix="$" />
          </Form.Item>
          <Form.Item
            name="enforcement"
            label="When the budget is exceeded"
            rules={[{ required: true }]}
          >
            <Select
              options={[
                { value: "alert", label: "Alert only — notify but keep serving" },
                { value: "block", label: "Hard block — refuse new AI calls" },
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
